import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import type { OfferPageRoomOption, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pane from 'components/Common/Pane'
import AlwaysOnRoomOptionMobile from '../AlwaysOnRoomOptionMobile'
import BodyText from 'LuxKit/Typography/BodyText'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOnMobileStickyHeaderFilters'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
`

interface Props {
  offer: App.HotelOffer
  packageOptions: Array<App.OfferPackageOptionView>
  roomOption?: OfferPageRoomOption
  onBack?: () => void
  onClose: () => void
}

function AlwaysOnRoomSelectionModal(props: Props) {
  const intl = useIntl()
  const {
    offer,
    packageOptions,
    roomOption,
    onBack,
    onClose,
  } = props

  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions)
  }, [packageOptions])

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'YHO1xH_BLriVjy8kZfL3',
          defaultMessage: 'Select a room',
          description: 'Title for always-on room selection modal dialog window',
        })}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AlwaysOnMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <>
          {packageOptionsByRoom.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'OKa2AnGXFncGRQDGAQ1q',
                    defaultMessage: '{count, plural, one {# room type} other {# room types}} available',
                    description: 'Count of available room types in always-on room selection modal',
                  },
                  { count: packageOptionsByRoom.length },
                )}
              </BodyText>
            </OptionsContainer>
          )}
          <Pane background="light-background">
            <VerticalSpacer gap={8}>
              {packageOptionsByRoom.map((item) => {
                if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
                  return null
                }
                return (<AlwaysOnRoomOptionMobile
                  key={item.roomId}
                  offer={offer}
                  packageViews={item.packageViews}
                  detailsModalMode="room-only"
                />)
              })}
            </VerticalSpacer>
          </Pane>
        </>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  offer: state.offer,
  rooms: state.rooms,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
}), AlwaysOnRoomSelectionModal)
